/* Copyright (c) 2019-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, NgZone } from '@angular/core';
import { merge, Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { isEmpty, setWith, unset } from 'lodash';
import { Commands } from 'bridge-commands/commands';
import { Types } from 'bridge-commands/types';
import { BrowserPlugin } from 'crimson/lib/bridge/browser-plugin/browser-plugin';
import { PluginHandlers, PluginResponse } from 'crimson/lib/bridge/common/plugin.models';
import { FullBridgeCommand } from 'crimson/lib/bridge/bridge.service';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { NativeEvents } from 'crimson/lib/native-events/native-events';
import { PlatformDetectorService } from 'crimson/lib/platform-detector/platform-detector.service';
import { ServiceWorkerService } from 'service-worker/lib/service-worker.service';
import { getEdenResumePayload, getRequestedAppControl } from 'utils/lib/platform/tizen';
import { PostMessageData } from 'service-workers/sw.types';
import { BrowserDbUpdateMessage } from '../models';
import { PluginMessages, SharedStorageUpdateMessages } from '../shared';

import OperationTypes = Types.Common.Sharedstorage.OperationType;
const WindowEvents = Commands.Common.Windowevents;

const createStructuredWindowEvent = (object: any) => `json:${JSON.stringify(object)}`;

@Injectable({
    providedIn: 'root'
})
export class WindoweventsPlugin extends BrowserPlugin {
    private windowEvents = new Subject<string>();
    private logger: Logger;
    private readonly defaultSharedStorageCache = {
        type: NativeEvents.SharedStorageUpdate
    };
    private sharedStorageCache: any = { ...this.defaultSharedStorageCache };

    constructor(
        loggingService: LoggingService,
        private swService: ServiceWorkerService,
        private ngZone: NgZone
    ) {
        super();
        this.logger = loggingService.getLogger('CommonBrowserPlugins/WindowEvents');
        this.logger.info('InstanceId: ', BrowserPlugin.browserInstanceId);

        PluginMessages.pipe(
            filter((obj: any) => obj.data === NativeEvents.IpcNewMessage),
            map((obj: any) => obj.data)
        ).subscribe(
            val => this.onInternalMessage(val),
            err => {}
        );

        merge(SharedStorageUpdateMessages, this.sharedStorageMessagesFromOtherTabs).subscribe(
            (data: BrowserDbUpdateMessage) => {
                this.onSharedStorageUpdate(data);
            },
            error => {
                this.logger.error('Error receiving update event from shared storage:', error);
            }
        );

        if (PlatformDetectorService.isPWAModeOnSamsungTV()) {
            ngZone.runOutsideAngular(() => {
                window.addEventListener('appcontrol', this.handleTizenAppControlEvent);
            });
        }
    }

    public get handlers(): PluginHandlers {
        return {
            module: WindowEvents.RegisterWindowEventsCallback.module,
            commands: {
                [WindowEvents.RegisterWindowEventsCallback.method]: this.registerWindowCallbacks.bind(this)
            }
        };
    }

    private get sharedStorageMessagesFromOtherTabs(): Observable<BrowserDbUpdateMessage> {
        return this.swService.swSharedStorageUpdate$.pipe(
            map((postMessage: PostMessageData) => JSON.parse(postMessage.content) as BrowserDbUpdateMessage),
            filter((message: BrowserDbUpdateMessage) => message.source !== BrowserPlugin.browserInstanceId)
        );
    }

    private onInternalMessage(message: string) {
        this.windowEvents.next(message);
    }

    private onSharedStorageUpdate(message: BrowserDbUpdateMessage) {
        switch (message.operationType) {
            case OperationTypes.Read:
                this.logger.verbose('Read operation (no-op)');
                break;
            case OperationTypes.Write:
                this.logger.verbose('Write operation:', message);
                setWith(this.sharedStorageCache, message.path, message.data, Object);
                break;
            case OperationTypes.Remove:
                this.logger.verbose('Remove operation:', message);
                unset(this.sharedStorageCache, message.path);
                break;
            case OperationTypes.Cache:
                this.logger.verbose('Cache operation:', message);
                this.sharedStorageCache = !isEmpty(message.data)
                    ? { ...this.sharedStorageCache, ...message.data }
                    : this.sharedStorageCache;
                break;
            default:
                this.logger.verbose('Invalid Operation Type:', message.operationType);
        }
        this.windowEvents.next(createStructuredWindowEvent(this.sharedStorageCache));
    }

    private registerWindowCallbacks(command: FullBridgeCommand<{}>): Observable<PluginResponse> {
        return this.makePersistentResult(this.windowEvents.asObservable());
    }

    private handleTizenAppControlEvent = () => {
        const { callerAppId, appControl } = getRequestedAppControl();
        this.logger.info(`Received appcontrol event from (${callerAppId}), operation: ${appControl.operation}`);

        const payload = appControl && getEdenResumePayload(appControl);
        if (!payload) {
            this.logger.error('Invalid payload in requested app control');
            return;
        }

        this.ngZone.run(() => {
            this.windowEvents.next(
                createStructuredWindowEvent({
                    type: NativeEvents.RouteChange,
                    'new-route': payload
                })
            );
        });
    };
}
